import { useI18n } from 'vue-i18n'

import { isNil } from 'lodash'
import { useI18nSafeFormat } from '~/composables/useI18nSafeFormat'
import type { Nil } from '~/types'
import { DealType, ValuationType } from '~/types/gql/generated/graphql'

/**
 * Used to regroup business rules that are unknown to the world of developers
 * Regrouping here the spicy cases help us prevent error in the future and mistakenly having deals -90% off!
 */
export function useBusinessRules() {
  const config = useRuntimeConfig()
  const { fmtCurrency } = useI18nSafeFormat()
  const { t } = useI18n()
  const resolveValuationType = (
    valuationType?: Nil<ValuationType>,
  ): {
    isNotSetYet: boolean
    isUncapped: boolean
    isCap: boolean
    isPreMoney: boolean
    isPostMoney: boolean
  } => {
    return {
      isNotSetYet: isNil(valuationType),
      isUncapped: valuationType === ValuationType.Uncapped,
      isCap: valuationType === ValuationType.Cap,
      isPreMoney: valuationType === ValuationType.PreMoney,
      isPostMoney: valuationType === ValuationType.PostMoney,
    }
  }

  function resolveValuationDisplayValue(
    valuation: Nil<number>,
    valuationType: Nil<ValuationType>,
    dealType: Nil<DealType>,
    currencyCode: Nil<string>,
    compact?: boolean,
  ): string | null {
    if (dealType === DealType.FeederFund)
      return null

    const { isNotSetYet, isUncapped } = resolveValuationType(valuationType)
    if (isNotSetYet || (isNil(valuation) && !isUncapped))
      return t('generics.not-set')

    if (isUncapped)
      return humanizeEnum(valuationType)

    const formattedAmount = fmtCurrency(valuation, currencyCode, compact)

    return [formattedAmount, humanizeEnum(valuationType, '-').toLowerCase()].join(' ')
  }

  const getResponsibleContacts = computed(() => config.public.BUSINESS_CONTACTS)

  return { resolveValuationType, resolveValuationDisplayValue, contacts: getResponsibleContacts }
}
